<template>
  <div class="document-list fill-height">
    <header class="list-header">
      <div class="list-title-block">
        <div class="list-eyebrow">{{ searchQuery ? "Globale Suche" : "Aktueller Bereich" }}</div>
        <div class="list-title-row">
          <h2>{{ folderName }}</h2>
          <span class="result-count">{{ documents.length }}</span>
        </div>
      </div>

      <div class="list-tools">
        <v-text-field
          v-model="localSearch"
          prepend-inner-icon="mdi-magnify"
          placeholder="Dokumente durchsuchen"
          aria-label="Dokumente durchsuchen"
          dark
          color="primary"
          dense
          outlined
          rounded
          clearable
          hide-details
          class="document-search"
          @click:clear="clearSearch"
          @keyup.enter="submitSearch">
          <template v-slot:append-outer>
            <v-btn icon small class="search-submit" aria-label="Suche starten" @click="submitSearch">
              <v-icon small>mdi-arrow-right</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <v-btn v-if="selectedItems.length > 0" color="primary" depressed rounded class="bulk-download" @click="downloadSelected">
          <v-icon left small>mdi-download-outline</v-icon>
          <span class="d-none d-sm-inline">Auswahl</span>
          <span class="selection-count">{{ selectedItems.length }}</span>
        </v-btn>
      </div>
    </header>

    <div class="table-surface">
      <!-- Desktop View: Data Table -->
      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        v-model="selectedItems"
        dark
        show-select
        sort-by="date"
        sort-desc
        :loading="loading"
        :items-per-page="50"
        :headers="headers"
        :items="documents"
        :footer-props="{ itemsPerPageText: 'Pro Seite', pageText: '{0}–{1} von {2}' }"
        item-key="_id"
        class="modern-document-table">
        <template v-slot:item="{ item, isSelected, select }">
          <tr
            class="document-row"
            :class="{ 'needs-action-row': needsMyAction(item) }"
            tabindex="0"
            draggable="true"
            @click.stop="$emit('open-document-actions', item)"
            @keydown.enter="$emit('open-document-actions', item)"
            @dragstart="onDragStart($event, item)">
            <td @click.stop>
              <v-simple-checkbox
                :value="isSelected"
                :aria-label="`${item.title || 'Dokument'} auswählen`"
                @input="select($event)"></v-simple-checkbox>
            </td>
            <td>
              <div class="date-cell" :style="{ color: getDateColor(item) }">
                <v-icon x-small :color="getDateColor(item) === 'inherit' ? 'grey lighten-1' : undefined">mdi-calendar-blank-outline</v-icon>
                <span>{{ formatDate(item.date) }}</span>
              </div>
            </td>
            <td>
              <span class="status-pill" :class="statusClass(item)">
                <v-icon x-small>{{ iconByStatus(item) }}</v-icon>
                {{ getStatusText(item) }}
              </span>
            </td>
            <td>
              <div class="document-title-cell">
                <span class="document-title text-truncate">{{ item.title || "Kein Titel" }}</span>
                <span v-if="needsMyAction(item)" class="action-note">Deine Aktion ist gefragt</span>
              </div>
            </td>
            <td>
              <span class="issuer-cell text-truncate">
                {{ item.invoiceIssuer ? item.invoiceIssuer.title : "Kein Rechnungssteller" }}
              </span>
            </td>
            <td>
              <div v-if="item.assignedUsers && item.assignedUsers.length > 0" class="avatar-stack">
                <v-tooltip v-for="(assignment, index) of item.assignedUsers.slice(0, 4)" :key="assignment._id || index" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-avatar v-bind="attrs" v-on="on" size="29" class="assignee-avatar">
                      <span>{{ getInitials(assignment.user) }}</span>
                    </v-avatar>
                  </template>
                  <span>{{ getUserName(assignment.user) }}</span>
                </v-tooltip>
                <span v-if="item.assignedUsers.length > 4" class="avatar-more">+{{ item.assignedUsers.length - 4 }}</span>
              </div>
              <span v-else class="empty-value">Nicht zugewiesen</span>
            </td>
          </tr>
        </template>
        <template v-slot:no-data>
          <div class="empty-state">
            <div class="empty-icon"><v-icon color="grey lighten-1">mdi-file-search-outline</v-icon></div>
            <strong>Keine Dokumente gefunden</strong>
            <span>{{ searchQuery ? "Passe den Suchbegriff an oder lösche die Suche." : "In diesem Bereich liegen aktuell keine Dokumente." }}</span>
            <v-btn v-if="searchQuery" small text color="primary" class="mt-2" @click="clearSearch">Suche zurücksetzen</v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Mobile View: Cards -->
      <v-data-iterator
        v-else
        dark
        :items="documents"
        :loading="loading"
        :items-per-page="20"
        :footer-props="{ itemsPerPageText: 'Pro Seite', pageText: '{0}–{1} von {2}', itemsPerPageOptions: [10, 20, 50] }"
        class="mobile-document-list">
        <template v-slot:default="{ items }">
          <div class="mobile-card-grid">
            <article
              v-for="item in items"
              :key="item._id"
              class="document-card"
              :class="{ 'needs-action-card': needsMyAction(item) }"
              tabindex="0"
              @click="$emit('open-document-actions', item)"
              @keydown.enter="$emit('open-document-actions', item)">
              <div class="card-topline">
                <span class="status-pill" :class="statusClass(item)">
                  <v-icon x-small>{{ iconByStatus(item) }}</v-icon>
                  {{ getStatusText(item) }}
                </span>
                <v-checkbox
                  v-model="selectedItems"
                  :value="item"
                  dense
                  hide-details
                  class="card-checkbox"
                  :aria-label="`${item.title || 'Dokument'} auswählen`"
                  @click.stop></v-checkbox>
              </div>

              <div class="card-main">
                <div class="file-icon" :class="statusClass(item)">
                  <v-icon>{{ iconByStatus(item) }}</v-icon>
                </div>
                <div class="card-copy">
                  <h3>{{ item.title || "Kein Titel" }}</h3>
                  <p>{{ item.invoiceIssuer ? item.invoiceIssuer.title : "Kein Rechnungssteller" }}</p>
                </div>
              </div>

              <div class="card-meta">
                <span :style="{ color: getDateColor(item) }">
                  <v-icon x-small>mdi-calendar-blank-outline</v-icon>
                  {{ formatDate(item.date) }}
                </span>
                <div v-if="item.assignedUsers && item.assignedUsers.length" class="avatar-stack">
                  <v-avatar
                    v-for="(assignment, index) in item.assignedUsers.slice(0, 3)"
                    :key="assignment._id || index"
                    size="25"
                    class="assignee-avatar">
                    <span>{{ getInitials(assignment.user) }}</span>
                  </v-avatar>
                  <span v-if="item.assignedUsers.length > 3" class="avatar-more">+{{ item.assignedUsers.length - 3 }}</span>
                </div>
                <span v-else class="empty-value">Nicht zugewiesen</span>
              </div>

              <div v-if="needsMyAction(item)" class="mobile-action-note">
                <v-icon x-small color="amber lighten-1">mdi-lightning-bolt</v-icon>
                Bitte prüfen und visieren
              </div>
            </article>
          </div>
        </template>
        <template v-slot:no-data>
          <div class="empty-state">
            <div class="empty-icon"><v-icon color="grey lighten-1">mdi-file-search-outline</v-icon></div>
            <strong>Keine Dokumente gefunden</strong>
            <span>{{ searchQuery ? "Passe den Suchbegriff an oder lösche die Suche." : "In diesem Bereich liegen aktuell keine Dokumente." }}</span>
          </div>
        </template>
      </v-data-iterator>
    </div>
  </div>
</template>

<script>
export default {
  name: "DocumentList",
  props: {
    documents: { type: Array, required: true },
    headers: { type: Array, required: true },
    folderName: { type: String, required: true },
    searchQuery: { type: String, required: true },
    loading: { type: Boolean, required: true },
  },
  data() {
    return {
      localSearch: this.searchQuery,
      selectedItems: [],
    }
  },
  watch: {
    searchQuery(newVal) {
      this.localSearch = newVal
    },
    documents() {
      this.selectedItems = []
    },
  },
  methods: {
    getInitials(name) {
      if (!name || typeof name !== "object") return "?"
      const namee = `${name.vorname || ""} ${name.nachname || ""}`.trim()
      if (!namee) return "?"
      return namee
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2)
    },
    getUserName(user) {
      if (!user || typeof user !== "object") return "Unbekannt"
      return `${user.vorname || ""} ${user.nachname || ""}`.trim() || "Unbekannt"
    },
    submitSearch() {
      this.localSearch = (this.localSearch || "").trim()
      this.$emit("update:searchQuery", this.localSearch)
      this.$nextTick(() => this.$emit("search-documents"))
    },
    clearSearch() {
      this.localSearch = ""
      this.$emit("update:searchQuery", "")
      this.$nextTick(() => this.$emit("search-documents"))
    },
    needsMyAction(item) {
      return item.userIsAssigned && item.approvalStatus !== "signed" && item.currentUserStatus !== "accepted"
    },
    statusClass(item) {
      if (item.approvalStatus === "signed") return "status-signed"
      if (item.approvalStatus === "declined") return "status-declined"
      if (item.currentUserStatus === "accepted") return "status-personal-done"
      return "status-open"
    },
    iconByStatus(item) {
      if (item.approvalStatus === "signed") return "mdi-check-circle"
      if (item.approvalStatus === "declined") return "mdi-close-circle"
      // NEU: Wenn das Dokument generell noch offen ist, du aber schon visiert hast
      if (item.currentUserStatus === "accepted") return "mdi-check-circle-outline"
      return "mdi-help-circle"
    },
    getStatusText(item) {
      if (item.approvalStatus === "signed") return "Visiert"
      if (item.approvalStatus === "declined") return "Abgelehnt"
      // NEU: Angepasster Text für dich
      if (item.currentUserStatus === "accepted") return "Von dir visiert"
      return "Offen"
    },
    getDateColor(item) {
      let status = item.approvalStatus
      if (status === "signed" || status === "declined") return "inherit"
      const diffDays = this.$moment().diff(this.$moment(item.date), "days")
      if (diffDays > 6) return "#E53935" // Red
      if (diffDays > 3) return "#FB8C00" // Orange
      return "inherit"
    },
    getIconColor(item) {
      if (item.approvalStatus === "signed") return "success"
      if (item.approvalStatus === "declined") return "error"
      // NEU: Grün, da es für dich erledigt ist
      if (item.currentUserStatus === "accepted") return "success"
      return "warning"
    },
    getRowStyle(item) {
      // NEU: Highlighting verschwindet, sobald du selbst visiert hast!
      if (this.needsMyAction(item)) {
        return "background-color: #363638;" // Graphit
      }
      return ""
    },
    onDragStart(e, item) {
      e.dataTransfer.setData("documentId", item._id)
      this.$emit("document-drag-start", e, item, this.selectedItems)
    },
    formatDate(date) {
      return date ? this.$moment(date).format("DD.MM.YY") : "-"
    },
    downloadSelected() {
      this.selectedItems.forEach((item) => {
        this.$emit("download-document", item)
      })
    },
  },
}
</script>

<style scoped>
.document-list {
  display: flex;
  min-height: 0;
  flex-direction: column;
}

.list-header {
  box-sizing: border-box;
  display: flex;
  flex: 0 0 auto;
  height: 68px;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  min-height: 68px;
  padding: 0 2px;
}

.list-title-block {
  position: relative;
  display: flex;
  height: 46px;
  min-width: 150px;
  flex-direction: column;
  justify-content: center;
}

.list-eyebrow {
  position: absolute;
  top: 0;
  left: 0;
  color: #8f8f93;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.15em;
  line-height: 1.2;
  text-transform: uppercase;
}

.list-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0;
}

.list-title-row h2 {
  max-width: 390px;
  margin: 0;
  overflow: hidden;
  color: #f4f4f5;
  font-size: 21px;
  font-weight: 750;
  line-height: 1.25;
  letter-spacing: -0.025em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-count,
.selection-count {
  display: inline-grid;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 999px;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.055);
  font-size: 11px;
  font-weight: 800;
}

.list-tools {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.document-search {
  color: #f4f4f5 !important;
  max-width: 420px;
}

.document-search ::v-deep .v-input__slot {
  min-height: 46px !important;
  border-radius: 15px !important;
  background: rgba(255, 255, 255, 0.035) !important;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.025);
}

.document-search ::v-deep fieldset {
  border-width: 1px !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
}

.document-search ::v-deep input {
  color: #f4f4f5 !important;
  caret-color: #009640 !important;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
}

.document-search ::v-deep input::placeholder {
  color: #a3a3a8 !important;
  opacity: 1 !important;
}

.document-search ::v-deep .v-input__prepend-inner .v-icon,
.document-search ::v-deep .v-input__append-inner .v-icon {
  color: #b8b8bc !important;
  opacity: 1 !important;
}

.document-search.v-input--is-focused ::v-deep .v-input__slot {
  background: rgba(255, 255, 255, 0.055) !important;
  box-shadow: inset 0 0 0 1px rgba(0, 150, 64, 0.18), 0 0 0 3px rgba(0, 150, 64, 0.1);
}

.document-search.v-input--is-focused ::v-deep fieldset {
  border-color: #009640 !important;
}

.document-search.v-input--is-focused ::v-deep .v-input__prepend-inner .v-icon,
.document-search.v-input--is-focused ::v-deep .v-input__append-inner .v-icon {
  color: #42c878 !important;
}

.document-search ::v-deep .v-input__append-outer {
  margin: 3px 0 0 7px !important;
}

.search-submit {
  width: 38px !important;
  height: 38px !important;
  border: 1px solid rgba(0, 150, 64, 0.28);
  color: #54cf83 !important;
  background: rgba(0, 150, 64, 0.1);
}

.search-submit:hover,
.search-submit:focus-visible {
  background: rgba(0, 150, 64, 0.18);
}

.bulk-download {
  min-height: 44px;
  padding-inline: 15px !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  background-color: #009640 !important;
  box-shadow: 0 8px 20px rgba(0, 150, 64, 0.16);
}

.bulk-download .selection-count {
  min-width: 21px;
  height: 21px;
  margin-left: 7px;
  padding: 0 5px;
  border-color: rgba(255, 255, 255, 0.22);
  color: white;
  background: rgba(255, 255, 255, 0.15);
}

.table-surface {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 18px 18px 0 0;
  background: rgba(0, 0, 0, 0.1);
}

.modern-document-table {
  height: 100%;
  color: #d4d4d8;
  background: transparent !important;
}

.modern-document-table ::v-deep .v-data-table__wrapper {
  max-height: calc(100% - 58px);
  overflow-y: auto;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  scrollbar-width: thin;
}

.modern-document-table ::v-deep thead th {
  position: sticky;
  z-index: 2;
  top: 0;
  height: 48px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09) !important;
  color: #8f8f93 !important;
  background: rgba(28, 28, 29, 0.92);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.modern-document-table ::v-deep tbody td {
  height: 66px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.065) !important;
  font-size: 13px;
}

.modern-document-table ::v-deep .v-data-footer {
  min-height: 56px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  color: #a3a3a8;
  background: rgba(0, 0, 0, 0.14);
}

.document-row {
  position: relative;
  cursor: pointer;
  outline: none;
  transition: background-color 150ms ease, box-shadow 150ms ease;
}

.document-row:hover,
.document-row:focus-visible {
  background: rgba(255, 255, 255, 0.045) !important;
}

.document-row:focus-visible {
  box-shadow: inset 0 0 0 2px rgba(0, 150, 64, 0.62);
}

.document-row.needs-action-row {
  background: linear-gradient(90deg, rgba(245, 158, 11, 0.1), rgba(255, 255, 255, 0.015)) !important;
  box-shadow: inset 3px 0 0 rgba(245, 158, 11, 0.9);
}

.date-cell {
  display: flex;
  min-width: 84px;
  align-items: center;
  gap: 7px;
  font-weight: 650;
}

.status-pill {
  display: inline-flex;
  min-height: 27px;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
}

.status-pill .v-icon {
  color: currentColor;
}

.status-signed {
  border-color: rgba(0, 150, 64, 0.3);
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.status-declined {
  border-color: rgba(248, 113, 113, 0.18);
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.1);
}

.status-personal-done {
  border-color: rgba(0, 150, 64, 0.25);
  color: #70d99a;
  background: rgba(0, 150, 64, 0.085);
}

.status-open {
  border-color: rgba(251, 191, 36, 0.18);
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.1);
}

.document-title-cell {
  display: flex;
  max-width: 330px;
  min-width: 170px;
  flex-direction: column;
  gap: 3px;
}

.document-title {
  color: #ececee;
  font-size: 13px;
  font-weight: 700;
}

.action-note {
  color: #fbbf24;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.issuer-cell {
  display: block;
  max-width: 210px;
  color: #a3a3a8;
}

.avatar-stack {
  display: flex;
  align-items: center;
  padding-left: 6px;
}

.assignee-avatar {
  margin-left: -7px;
  border: 2px solid #232324;
  color: #e4e4e7;
  background: linear-gradient(145deg, #444446, #303032) !important;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.avatar-more {
  margin-left: 5px;
  color: #8f8f93;
  font-size: 10px;
  font-weight: 700;
}

.empty-value {
  color: #8f8f93;
  font-size: 11px;
}

.empty-state {
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 36px 20px;
  color: #8f8f93;
  text-align: center;
}

.empty-state strong {
  margin-top: 13px;
  color: #d4d4d8;
  font-size: 15px;
}

.empty-state span {
  max-width: 360px;
  margin-top: 5px;
  font-size: 12px;
  line-height: 1.5;
}

.empty-icon {
  display: grid;
  width: 54px;
  height: 54px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.04);
}

.mobile-document-list {
  min-height: 300px;
  padding: 10px;
  background: transparent !important;
}

.mobile-document-list ::v-deep .v-data-footer {
  margin-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  color: #a3a3a8;
}

.mobile-card-grid {
  display: grid;
  gap: 10px;
}

.document-card {
  position: relative;
  padding: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  outline: none;
  background: rgba(255, 255, 255, 0.035);
  transition: border-color 150ms ease, background-color 150ms ease, transform 150ms ease;
}

.document-card:active {
  transform: scale(0.995);
}

.document-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.66);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.document-card.needs-action-card {
  border-color: rgba(245, 158, 11, 0.2);
  box-shadow: inset 3px 0 0 rgba(245, 158, 11, 0.88);
}

.card-topline,
.card-main,
.card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.card-checkbox {
  flex: 0 0 auto;
  margin: -6px -4px -6px 0 !important;
  padding: 0 !important;
}

.card-main {
  justify-content: flex-start;
  margin-top: 13px;
}

.file-icon {
  display: grid;
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 1px solid currentColor;
  border-radius: 13px;
  background: rgba(0, 0, 0, 0.18);
}

.file-icon .v-icon {
  color: currentColor;
  font-size: 22px;
}

.card-copy {
  min-width: 0;
}

.card-copy h3 {
  margin: 0;
  overflow: hidden;
  color: #f4f4f5;
  font-size: 14px;
  font-weight: 750;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-copy p {
  margin: 3px 0 0;
  overflow: hidden;
  color: #a3a3a8;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-meta {
  margin-top: 14px;
  padding-top: 11px;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  color: #a3a3a8;
  font-size: 10px;
  font-weight: 650;
}

.card-meta > span {
  display: flex;
  align-items: center;
  gap: 5px;
}

.mobile-action-note {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  color: #fbbf24;
  font-size: 10px;
  font-weight: 700;
}

.cursor-pointer {
  cursor: pointer;
}

@media (max-width: 959px) {
  .document-list {
    height: auto !important;
  }

  .list-header {
    height: auto;
    min-height: 0;
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
    padding: 2px 2px 14px;
  }

  .list-title-block {
    position: static;
    height: auto;
  }

  .list-eyebrow {
    position: static;
  }

  .list-title-row {
    margin-top: 3px;
  }

  .list-tools {
    width: 100%;
  }

  .document-search {
    max-width: none;
  }

  .table-surface {
    flex: 0 0 auto;
    overflow: visible;
    border-radius: 16px;
  }
}

@media (max-width: 599px) {
  .list-title-row h2 {
    max-width: calc(100vw - 120px);
    font-size: 19px;
  }

  .list-tools {
    gap: 7px;
  }

  .document-search ::v-deep .v-input__slot {
    min-height: 44px !important;
  }

  .document-search ::v-deep .v-input__append-outer {
    display: none;
  }

  .bulk-download {
    min-width: 48px !important;
    padding-inline: 11px !important;
  }

  .mobile-document-list {
    padding: 8px;
  }

  .document-card {
    border-color: rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.045);
  }
}
</style>
